import Skeleton from '@mui/material/Skeleton';
import type { JSX } from 'react';

import { DonutChart } from 'Components/BaseCharts/DonutChart';
import { Text } from 'Components/Text/Text';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import { RiskRatingsData } from '../DueDiligenceDashboard.helpers';
import styles from '../DueDiligenceDashboard.module.css';

/**
 * @param chartName The chart to render, which also determines whether the label below it says "Assessed" (control effectiveness) or "Scored" (risk).
 * @param onClick Called with the name of the clicked chart segment.
 * @param chartData While `undefined`, skeletons are rendered in place of the chart and its label.
 */
export interface DonutChartsProps {
    chartName: 'Inherent Risk' | 'Control Effectiveness' | 'Residual Risk';
    module: DueDiligenceModule;
    onClick: (item: string) => void;

    chartData?: RiskRatingsData;
}

/**
 * Renders one of three charts, depending on `chartName`, along with a label of how many of the module's active assessment subjects (services or use cases) have been scored or assessed:
 * 1. A donut that categorizes active assessment subjects by inherent risk score.
 * 2. A donut that categorizes active assessment subjects by control effectiveness.
 * 3. A donut that categorizes active assessment subjects by residual risk score.
 */
export const DonutCharts = (props: DonutChartsProps): JSX.Element => {
    const subjectsLabel = props.module === ProductModule.UCRM ? 'Use Cases' : 'Services';

    return (
        /** This top-level div may seem pointless, but the content overflows out of its parent if a fragment is used instead. */
        <div>
            {props.chartData ? (
                <DonutChart name={props.chartName} data={props.chartData.data} average={props.chartData.average} averageLabel={props.chartData.averageLabel} onChartClick={props.onClick} />
            ) : (
                <div className={styles.chartLabelRow}>
                    <Skeleton sx={{ width: '250px', height: '250px' }} variant="circular" />
                </div>
            )}
            <div className={styles.chartLabelRow}>
                {props.chartData ? (
                    <Text variant="Header4" color="lightGray">
                        {props.chartData.numberOfScoredAssessmentSubjects} of {props.chartData.numberOfAssessmentSubjects} {subjectsLabel} {props.chartName === 'Control Effectiveness' ? 'Assessed' : 'Scored'}
                    </Text>
                ) : (
                    <Skeleton>
                        <Text variant="Header4" color="lightGray">
                            --- of --- {subjectsLabel}
                        </Text>
                    </Skeleton>
                )}
            </div>
        </div>
    );
};
